"use client";

import { useEffect, useRef } from "react";
import { shouldRetireServiceWorker } from "@/lib/serviceWorkerPolicy";

/**
 * Forces the browser to check for a new service worker whenever the user
 * focuses the tab. The browser's own update check can be up to 24 hours —
 * without this, a deployed fix can sit unclaimed for an entire day even
 * though skipWaiting + clientsClaim are set in sw.ts.
 */
export function SwUpdateListener() {
  const checkedOnMount = useRef(false);

  useEffect(() => {
    // guard: SSR and environments without service workers
    if (!("serviceWorker" in navigator)) return;

    // The beta hostname runs `next dev`; Serwist is disabled there, but a
    // production worker installed during an earlier build can remain in the
    // browser and serve year-cached Turbopack chunks. Remove the worker and
    // its origin caches once, then reload into a genuinely network-fresh beta.
    if (shouldRetireServiceWorker(window.location.hostname)) {
      let cancelled = false;
      void (async () => {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const cacheNames = "caches" in window ? await window.caches.keys() : [];
        await Promise.all([
          ...registrations.map((registration) => registration.unregister()),
          ...cacheNames.map((cacheName) => window.caches.delete(cacheName)),
        ]);

        const changed = registrations.length > 0 || cacheNames.length > 0;
        const reloadKey = "wtt-beta-service-worker-retired";
        if (!cancelled && changed && sessionStorage.getItem(reloadKey) !== "1") {
          sessionStorage.setItem(reloadKey, "1");
          window.location.reload();
        }
      })().catch(() => {});
      return () => {
        cancelled = true;
      };
    }

    // debounce: skip re-checks within 30 seconds
    let lastCheck = 0;
    const MIN_INTERVAL_MS = 30_000;

    async function checkForUpdate() {
      const now = Date.now();
      if (now - lastCheck < MIN_INTERVAL_MS) return;
      lastCheck = now;

      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map((reg) => reg.update().catch(() => {}))
        );
      } catch {
        // getRegistrations or update can throw in insecure contexts
      }
    }

    // 1. Check once on mount (after a short delay so the page settles)
    if (!checkedOnMount.current) {
      checkedOnMount.current = true;
      setTimeout(checkForUpdate, 3000);
    }

    // 2. Check every time the user returns to this tab
    function onFocus() {
      checkForUpdate();
    }
    window.addEventListener("focus", onFocus);

    // 3. Fallback: also poll every 5 minutes for tabs that stay open
    const interval = setInterval(checkForUpdate, 5 * 60_000);

    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, []);

  // This component renders nothing — it's purely a side-effect harness
  return null;
}
